import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Rating,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  AutoAwesome as SparklesIcon,
  Check as CheckIcon,
  UploadFile as UploadFileIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const proficiencyLevels = [
  { value: 1, label: 'Beginner' },
  { value: 2, label: 'Intermediate' },
  { value: 3, label: 'Advanced' },
  { value: 4, label: 'Expert' },
  { value: 5, label: 'Master' }
];

const priorityLevels = ['Low', 'Medium', 'High', 'Critical'];

export default function SkillMatrixPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [projectSkills, setProjectSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [editingSkill, setEditingSkill] = useState(null);
  const [projectName, setProjectName] = useState('');

  // Resource Matching State
  const [openMatchDialog, setOpenMatchDialog] = useState(false);
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(false);

  // Assignment State
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [assigningCandidate, setAssigningCandidate] = useState(null);
  const [openAssignConfirmDialog, setOpenAssignConfirmDialog] = useState(false);
  const [assignmentDetails, setAssignmentDetails] = useState({
    role: '',
    allocation: 100,
    startDate: '',
    endDate: ''
  });
  
  const [newSkill, setNewSkill] = useState({
    skillName: '',
    category: '',
    requiredProficiency: 3,
    priority: 'Medium',
    notes: '',
    quantity: 1,
    experienceYears: 0,
    allocationPercentage: 100,
    startDate: '',
    endDate: '',
    domain: ''
  });

  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importErrors, setImportErrors] = useState([]);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    if (projectId) {
      fetchProjectSkills();
      fetchProjectDetails();
      fetchTeams();
    } else {
      //console.error('No projectId found in URL params');
      setLoading(false);
    }
  }, [projectId]);

  const fetchProjectDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showSnackbar('Authentication required', 'error');
        return;
      }

      const response = await axios.get(
        `${API_URL}/api/projects/${projectId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.status === 'success') {
        setProjectName(response.data.data.name);
      }
    } catch (error) {
      //console.error('Error fetching project details:', error);
      showSnackbar('Failed to load project details', 'error');
    }
  };

  const fetchTeams = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(`${API_URL}/api/addteam/members/teams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeams(response.data.teams || []);
      if (response.data.teams?.length > 0) {
        setSelectedTeamId(response.data.teams[0].team_id);
      }
    } catch (error) {
      //console.error('Error fetching teams:', error);
    }
  };

  const fetchProjectSkills = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        showSnackbar('Authentication required', 'error');
        setLoading(false);
        return;
      }

      const response = await axios.get(
        `${API_URL}/api/projects/${projectId}/skills`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.status === 'success') {
        setProjectSkills(response.data.data || []);
      }
    } catch (error) {
      //console.error('Error fetching skills:', error);
      if (error.response?.status !== 404) {
        showSnackbar('Failed to load skills', 'error');
      }
      setProjectSkills([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddDialog = () => {
    setNewSkill({
      skillName: '',
      category: '',
      requiredProficiency: 3,
      priority: 'Medium',
      notes: '',
      quantity: 1,
      experienceYears: 0,
      allocationPercentage: 100,
      startDate: '',
      endDate: '',
      domain: ''
    });
    setEditingSkill(null);
    setOpenAddDialog(true);
  };

  const handleOpenEditDialog = (skill) => {
    setNewSkill({
      skillName: skill.skillName || '',
      category: skill.category || '',
      requiredProficiency: skill.requiredProficiency || 3,
      priority: skill.priority || 'Medium',
      notes: skill.notes || '',
      quantity: skill.quantity || 1,
      experienceYears: skill.experienceYears || 0,
      allocationPercentage: skill.allocationPercentage || 100,
      startDate: skill.startDate ? skill.startDate.substring(0, 10) : '',
      endDate: skill.endDate ? skill.endDate.substring(0, 10) : '',
      domain: skill.domain || ''
    });
    setEditingSkill(skill);
    setOpenAddDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenAddDialog(false);
    setEditingSkill(null);
    setNewSkill({
      skillName: '',
      category: '',
      requiredProficiency: 3,
      priority: 'Medium',
      notes: '',
      quantity: 1,
      experienceYears: 0,
      allocationPercentage: 100,
      startDate: '',
      endDate: '',
      domain: ''
    });
  };

  const handleSaveSkill = async () => {
    if (!newSkill.skillName.trim()) {
      showSnackbar('Skill name is required', 'error');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;

      const skillData = {
        ...newSkill,
        project_id: projectId,
        company_id
      };

      if (editingSkill) {
        const response = await axios.put(
          `${API_URL}/api/projects/${projectId}/skills/${editingSkill.skill_id}`,
          skillData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
            }
          }
        );

        if (response.data.status === 'success') {
          showSnackbar('Resource requirement updated successfully', 'success');
          fetchProjectSkills();
        }
      } else {
        const response = await axios.post(
          `${API_URL}/api/projects/${projectId}/skills`,
          skillData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
            }
          }
        );

        if (response.data.status === 'success') {
          showSnackbar('Resource requirement added successfully', 'success');
          fetchProjectSkills();
        }
      }

      handleCloseDialog();
    } catch (error) {
      //console.error('Error saving skill:', error);
      showSnackbar(
        error.response?.data?.message || 'Failed to save resource requirement',
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSkill = async (skillId) => {
    if (!window.confirm('Are you sure you want to delete this resource requirement?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${API_URL}/api/projects/${projectId}/skills/${skillId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );

      if (response.data.status === 'success') {
        showSnackbar('Resource requirement deleted successfully', 'success');
        fetchProjectSkills();
      }
    } catch (error) {
      //console.error('Error deleting skill:', error);
      showSnackbar('Failed to delete resource requirement', 'error');
    }
  };

  // Find Resources Flow
  const handleFindResources = async (requirement) => {
    setSelectedRequirement(requirement);
    setOpenMatchDialog(true);
    setLoadingMatches(true);
    setMatches([]);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/resource-planning/match/${requirement.skill_id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (response.data.success) {
        setMatches(response.data.data || []);
      }
    } catch (error) {
      //console.error('Error matching resources:', error);
      showSnackbar('Failed to match resources', 'error');
    } finally {
      setLoadingMatches(false);
    }
  };

  const handleOpenAssignConfirm = (candidate) => {
    setAssigningCandidate(candidate);
    setAssignmentDetails({
      role: selectedRequirement.category || selectedRequirement.skillName || 'Developer',
      allocation: selectedRequirement.allocationPercentage || 100,
      startDate: selectedRequirement.startDate ? selectedRequirement.startDate.substring(0, 10) : '',
      endDate: selectedRequirement.endDate ? selectedRequirement.endDate.substring(0, 10) : ''
    });
    setOpenAssignConfirmDialog(true);
  };

  const handleConfirmAssignment = async () => {
    if (!selectedTeamId) {
      showSnackbar('Please select a team for assignment', 'error');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const assignBody = {
        projectId: projectId,
        teamId: selectedTeamId,
        teamMembers: [
          {
            employeeId: assigningCandidate.employeeId,
            teamId: selectedTeamId,
            role: assignmentDetails.role,
            allocation: assignmentDetails.allocation,
            startDate: assignmentDetails.startDate,
            endDate: assignmentDetails.endDate || null
          }
        ]
      };
      
      const response = await axios.post(
        `${API_URL}/api/addteam`,
        assignBody,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      showSnackbar('Candidate assigned to project team successfully!', 'success');
      setOpenAssignConfirmDialog(false);
      setOpenMatchDialog(false);
    } catch (error) {
      //console.error('Error assigning member:', error);
      showSnackbar(error.response?.data?.message || 'Failed to assign candidate', 'error');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/projects-skills/migration/template`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `resource_requirements_template.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      //console.error('Error downloading template:', error);
      showSnackbar('Failed to download template', 'error');
    }
  };

  const handleUploadExcel = async () => {
    if (!importFile) {
      showSnackbar('Please select an Excel file to upload', 'error');
      return;
    }
    setImporting(true);
    setImportErrors([]);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', importFile);
      
      const response = await axios.post(
        `${API_URL}/api/projects-skills/migration/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.status === 'success') {
        showSnackbar(response.data.message || 'Requirements imported successfully!', 'success');
        setOpenImportDialog(false);
        setImportFile(null);
        fetchProjectSkills();
      }
    } catch (error) {
      //console.error('Error importing Excel:', error);
      if (error.response?.data?.errors) {
        setImportErrors(error.response.data.errors);
        showSnackbar('Excel validation failed. Please fix errors and retry.', 'error');
      } else {
        showSnackbar(error.response?.data?.message || 'Failed to import requirements', 'error');
      }
    } finally {
      setImporting(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getProficiencyLabel = (level) => {
    const found = proficiencyLevels.find(p => p.value === level);
    return found ? found.label : 'Unknown';
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical': return 'error';
      case 'High': return 'warning';
      case 'Medium': return 'info';
      case 'Low': return 'success';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading resource requirements...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Resource Requirements
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Project: {projectName || 'Loading...'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<UploadFileIcon />}
              onClick={() => setOpenImportDialog(true)}
            >
              Import Excel
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenAddDialog}
            >
              Add Resource Requirement
            </Button>
          </Box>
        </Box>

        {projectSkills.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Resource Requirements added yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Define the skills, headcount, experience, and timeline required for this project
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Skill / Role Name</strong></TableCell>
                  <TableCell><strong>Category</strong></TableCell>
                  <TableCell><strong>Domain</strong></TableCell>
                  <TableCell><strong>Quantity</strong></TableCell>
                  <TableCell><strong>Proficiency Required</strong></TableCell>
                  <TableCell><strong>Min Exp (Yrs)</strong></TableCell>
                  <TableCell><strong>Allocation %</strong></TableCell>
                  <TableCell><strong>Duration</strong></TableCell>
                  <TableCell><strong>Priority</strong></TableCell>
                  <TableCell><strong>Notes</strong></TableCell>
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {projectSkills.map((skill) => (
                  <TableRow key={skill.skill_id} hover>
                    <TableCell>
                      <Typography variant="body1" fontWeight="medium">
                        {skill.skillName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={skill.category || 'General'} size="small" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {skill.domain || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1" fontWeight="bold" color="primary">
                        {skill.quantity || 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Rating value={skill.requiredProficiency} readOnly size="small" />
                        <Typography variant="body2" color="text.secondary">
                          ({getProficiencyLabel(skill.requiredProficiency)})
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {skill.experienceYears ? `${skill.experienceYears} yrs` : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={`${skill.allocationPercentage || 100}%`} 
                        variant="outlined" 
                        color="secondary"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" display="block">
                        {skill.startDate ? new Date(skill.startDate).toLocaleDateString() : 'Start'}
                        {' → '}
                        {skill.endDate ? new Date(skill.endDate).toLocaleDateString() : 'End'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={skill.priority} 
                        color={getPriorityColor(skill.priority)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {skill.notes || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleFindResources(skill)}
                        color="secondary"
                        title="Find Matching Resources"
                        sx={{ mr: 0.5 }}
                      >
                        <SparklesIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEditDialog(skill)}
                        color="primary"
                        sx={{ mr: 0.5 }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteSkill(skill.skill_id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Requirement Form Dialog */}
      <Dialog open={openAddDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingSkill ? 'Edit Resource Requirement' : 'Add Resource Requirement'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Skill / Role Name"
                fullWidth
                required
                value={newSkill.skillName}
                onChange={(e) => setNewSkill({ ...newSkill, skillName: e.target.value })}
                placeholder="e.g., Senior Java Developer, React Engineer, Python"
              />

              <TextField
                label="Category"
                fullWidth
                value={newSkill.category}
                onChange={(e) => setNewSkill({ ...newSkill, category: e.target.value })}
                placeholder="e.g., Programming Language, Framework"
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Target Domain Requirement"
                fullWidth
                value={newSkill.domain}
                onChange={(e) => setNewSkill({ ...newSkill, domain: e.target.value })}
                placeholder="e.g., Banking, Healthcare, Retail"
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Quantity Needed (Headcount)"
                type="number"
                fullWidth
                inputProps={{ min: 1 }}
                value={newSkill.quantity}
                onChange={(e) => setNewSkill({ ...newSkill, quantity: parseInt(e.target.value) || 1 })}
              />

              <TextField
                label="Required Experience (Years)"
                type="number"
                fullWidth
                inputProps={{ min: 0, step: 0.5 }}
                value={newSkill.experienceYears}
                onChange={(e) => setNewSkill({ ...newSkill, experienceYears: parseFloat(e.target.value) || 0 })}
              />

              <TextField
                label="Allocation % Needed"
                type="number"
                fullWidth
                inputProps={{ min: 10, max: 100, step: 5 }}
                value={newSkill.allocationPercentage}
                onChange={(e) => setNewSkill({ ...newSkill, allocationPercentage: parseInt(e.target.value) || 100 })}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Start Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={newSkill.startDate}
                onChange={(e) => setNewSkill({ ...newSkill, startDate: e.target.value })}
              />

              <TextField
                label="End Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={newSkill.endDate}
                onChange={(e) => setNewSkill({ ...newSkill, endDate: e.target.value })}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Required Proficiency Level</InputLabel>
                <Select
                  value={newSkill.requiredProficiency}
                  onChange={(e) => setNewSkill({ ...newSkill, requiredProficiency: e.target.value })}
                  label="Required Proficiency Level"
                >
                  {proficiencyLevels.map((level) => (
                    <MenuItem key={level.value} value={level.value}>
                      {level.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={newSkill.priority}
                  onChange={(e) => setNewSkill({ ...newSkill, priority: e.target.value })}
                  label="Priority"
                >
                  {priorityLevels.map((priority) => (
                    <MenuItem key={priority} value={priority}>
                      {priority}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <TextField
              label="Notes / Special Requirements"
              fullWidth
              multiline
              rows={3}
              value={newSkill.notes}
              onChange={(e) => setNewSkill({ ...newSkill, notes: e.target.value })}
              placeholder="Additional information about domain requirements or certifications needed"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSaveSkill}
            variant="contained"
            disabled={saving || !newSkill.skillName.trim()}
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {saving ? 'Saving...' : editingSkill ? 'Update' : 'Add Requirement'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Match Results Dialog */}
      <Dialog open={openMatchDialog} onClose={() => setOpenMatchDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SparklesIcon color="secondary" />
          <Typography variant="h6" component="span" fontWeight="bold">
            Recommended Resources for {selectedRequirement?.skillName}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {loadingMatches ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
              <CircularProgress color="secondary" />
              <Typography>Finding matches using Skill Match Engine...</Typography>
            </Box>
          ) : matches.length === 0 ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No active employees matched the minimum proficiency level ({selectedRequirement?.requiredProficiency}/5) or experience ({selectedRequirement?.experienceYears} yrs) requirements.
              </Typography>
            </Box>
          ) : (
            <List sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {matches.map((candidate, idx) => (
                <Paper key={candidate.employeeId} variant="outlined" sx={{ p: 2, hover: { boxShadow: 1 } }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={8}>
                      <Typography variant="h6" fontWeight="bold">
                        {idx + 1}. {candidate.employeeName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {candidate.designation} — {candidate.department}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        <Chip label={`Skill score: ${candidate.skillScore}%`} size="small" variant="outlined" color="primary" />
                        <Chip label={`Experience score: ${candidate.experienceScore}%`} size="small" variant="outlined" color="info" />
                        <Chip label={`Domain score: ${candidate.domainScore}%`} size="small" variant="outlined" color="success" />
                        <Chip 
                          label={`Availability: ${candidate.availability}% (${candidate.availableHours}h free)`} 
                          size="small" 
                          variant="outlined" 
                          color={candidate.availability > 50 ? "success" : candidate.availability > 0 ? "warning" : "error"} 
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', sm: 'flex-end' }, gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                        <Typography variant="h4" fontWeight="bold" color="secondary.main">
                          {candidate.matchScore}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                          % Match
                        </Typography>
                      </Box>
                      {/* <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        startIcon={<CheckIcon />}
                        onClick={() => handleOpenAssignConfirm(candidate)}
                      >
                        Assign
                      </Button> */}
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenMatchDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Assignment Confirmation Dialog */}
      <Dialog open={openAssignConfirmDialog} onClose={() => setOpenAssignConfirmDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Assignment</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              Assign <strong>{assigningCandidate?.employeeName}</strong> to the project team.
            </Typography>

            <FormControl fullWidth>
              <InputLabel>Select Team</InputLabel>
              <Select
                value={selectedTeamId}
                onChange={(e) => setSelectedTeamId(e.target.value)}
                label="Select Team"
              >
                {teams.map((t) => (
                  <MenuItem key={t.team_id} value={t.team_id}>
                    {t.team_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Assigned Project Role"
              fullWidth
              value={assignmentDetails.role}
              onChange={(e) => setAssignmentDetails({ ...assignmentDetails, role: e.target.value })}
            />

            <TextField
              label="Allocation Percentage (%)"
              type="number"
              fullWidth
              inputProps={{ min: 10, max: 100 }}
              value={assignmentDetails.allocation}
              onChange={(e) => setAssignmentDetails({ ...assignmentDetails, allocation: parseInt(e.target.value) || 100 })}
            />

            <TextField
              label="Start Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={assignmentDetails.startDate}
              onChange={(e) => setAssignmentDetails({ ...assignmentDetails, startDate: e.target.value })}
            />

            <TextField
              label="End Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={assignmentDetails.endDate}
              onChange={(e) => setAssignmentDetails({ ...assignmentDetails, endDate: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAssignConfirmDialog(false)}>Cancel</Button>
          <Button onClick={handleConfirmAssignment} variant="contained" color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Excel Requirements Dialog */}
      <Dialog open={openImportDialog} onClose={() => { setOpenImportDialog(false); setImportFile(null); setImportErrors([]); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Import Resource Requirements</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Upload an Excel workbook containing resource requirement records. Each requirement will be linked to its corresponding project using the Project Key.
            </Typography>

            <Button
              variant="outlined"
              color="secondary"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadTemplate}
              sx={{ alignSelf: 'flex-start' }}
            >
              Download Excel Template
            </Button>

            <Box sx={{ border: '2px dashed #CBD5E1', borderRadius: 2, p: 3, textAlign: 'center', bgcolor: '#F8FAFC', cursor: 'pointer', mt: 1 }}>
              <input
                accept=".xlsx, .xls"
                style={{ display: 'none' }}
                id="excel-file-upload-input"
                type="file"
                onChange={(e) => setImportFile(e.target.files[0])}
              />
              <label htmlFor="excel-file-upload-input" style={{ cursor: 'pointer' }}>
                <UploadFileIcon sx={{ fontSize: 40, color: '#6366F1', mb: 1 }} />
                <Typography variant="body2" fontWeight="bold">
                  {importFile ? importFile.name : 'Click to select Excel file (.xlsx)'}
                </Typography>
                {!importFile && (
                  <Typography variant="caption" color="text.secondary">
                    Supports 500+ records bulk load
                  </Typography>
                )}
              </label>
            </Box>

            {importErrors.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                  Validation Checklist Corrections:
                </Alert>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200, overflow: 'auto' }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#FEF2F2' }}>
                      <TableRow>
                        <TableCell><strong>Row</strong></TableCell>
                        <TableCell><strong>Error Description</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {importErrors.map((err, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ color: '#DC2626', fontWeight: 'bold' }}>{err.row}</TableCell>
                          <TableCell sx={{ color: '#7F1D1D' }}>{err.error}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenImportDialog(false); setImportFile(null); setImportErrors([]); }}>Cancel</Button>
          <Button
            onClick={handleUploadExcel}
            variant="contained"
            color="primary"
            disabled={!importFile || importing}
            startIcon={importing ? <CircularProgress size={20} /> : <SparklesIcon />}
          >
            {importing ? 'Importing...' : 'Upload & Process'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}